import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authService from '../../services/authService';
import logger from '../../utils/logger';
import storage from '../../utils/LocalStorageManager';

// 异步 thunk 用于初始化身份验证状态
export const initAuth = createAsyncThunk(
  'auth/init',
  async (_, { dispatch }) => {
    try {
      logger.log('[Auth] 开始初始化认证状态');
      const authData = authService.getAuthDataFromStorage();
      
      if (authData) {
        logger.log('[Auth] 发现本地存储的认证数据');
        try {
          // 如果有令牌，尝试验证并获取最新的用户信息
          await dispatch(fetchUserProfile(authData.token));
          logger.log('[Auth] 成功验证并更新用户信息');
          return authData;
        } catch (error) {
          logger.error('[Auth] 验证令牌失败:', error);
          // 清除本地存储
          authService.removeAuthData();
          return { user: null, token: null };
        }
      }
      
      logger.log('[Auth] 未找到本地存储的认证数据');
      return { user: null, token: null };
    } catch (error) {
      logger.error('[Auth] 初始化认证状态时发生错误:', error);
      // 确保清除可能损坏的认证数据
      authService.removeAuthData();
      return { user: null, token: null };
    }
  }
);

// 获取用户个人资料
export const fetchUserProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (token, { rejectWithValue }) => {
    try {
      const data = await authService.getUserProfile(token);
      
      // 更新本地存储中的用户信息
      authService.saveAuthData(data.user, token);
      
      return { user: data.user, token };
    } catch (error) {
      logger.error('获取用户资料错误:', error);
      return rejectWithValue(error.message || '网络错误，无法获取用户资料');
    }
  }
);

// 登录 thunk
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { dispatch, rejectWithValue }) => {
    try {
      // 只进行登录，不同步数据
      const data = await authService.login(credentials);
      
      // 保存认证数据到本地存储
      authService.saveAuthData(data.user, data.token);
      
      return { user: data.user, token: data.token };
    } catch (error) {
      logger.error('登录错误:', error);
      return rejectWithValue(error.message || '登录失败，请稍后重试');
    }
  }
);

// 单独的数据同步 thunk
export const syncUserData = createAsyncThunk(
  'auth/syncData',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const result = await authService.syncData(dispatch);
      return result;
    } catch (error) {
      logger.error('数据同步错误:', error);
      return rejectWithValue(error.message || '数据同步失败');
    }
  }
);

// 登录并同步数据 thunk - 保留但不使用，用于向后兼容
export const loginAndSyncData = createAsyncThunk(
  'auth/loginAndSync',
  async (credentials, { dispatch, rejectWithValue }) => {
    try {
      // 先执行登录
      const loginResult = await dispatch(loginUser(credentials)).unwrap();
      // 登录成功后在后台同步数据
      dispatch(syncUserData());
      return loginResult;
    } catch (error) {
      logger.error('登录并同步数据错误:', error);
      return rejectWithValue(error.message || '网络错误，无法登录或同步数据');
    }
  }
);

// 注册 thunk
export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { dispatch, rejectWithValue }) => {
    try {
      // userData 现在包含 email, password, code 和可选的 username
      const data = await authService.register(userData);
      
      // 注册成功
      if (data.user && data.token) {
        return { user: data.user, token: data.token };
      }
      
      // 如果后端没有返回用户信息，则返回注册成功的标志
      return { registered: true };
    } catch (error) {
      logger.error('注册错误:', error);
      return rejectWithValue(error.message || '网络错误，无法注册');
    }
  }
);

// 发送验证码 thunk
export const sendVerificationCode = createAsyncThunk(
  'auth/sendCode',
  async (email, { rejectWithValue }) => {
    try {
      const data = await authService.sendVerificationCode(email);
      return data;
    } catch (error) {
      logger.error('发送验证码错误:', error);
      return rejectWithValue(error.message || '网络错误，无法发送验证码');
    }
  }
);

// 验证验证码 thunk
export const verifyCode = createAsyncThunk(
  'auth/verifyCode',
  async ({ email, code }, { rejectWithValue }) => {
    try {
      const data = await authService.verifyEmailCode(email, code);
      return data;
    } catch (error) {
      logger.error('验证码验证错误:', error);
      return rejectWithValue(error.message || '验证码验证失败');
    }
  }
);

// 重置密码 thunk
export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email, code, password }, { rejectWithValue }) => {
    try {
      const data = await authService.resetPassword(email, code, password);
      return data;
    } catch (error) {
      logger.error('重置密码错误:', error);
      return rejectWithValue(error.message || '密码重置失败');
    }
  }
);

// 登出 thunk
export const logoutUser = createAsyncThunk(
  'auth/logout',
  async () => {
    logger.log("执行Redux中的logout函数");
    
    // 清除本地存储
    authService.removeAuthData();
    
    // 清除同步标记
    storage.removeItem(authService.DATA_SYNCED_KEY);
    
    logger.log('清除存储完成:',
      'token存在:', !!storage.getAuthToken(),
      '用户信息存在:', !!storage.getUserInfo());
    
    return true;
  }
);

// 更新用户信息 thunk
export const updateUserData = createAsyncThunk(
  'auth/updateUser',
  async (newUserData, { getState, rejectWithValue }) => {
    const { auth } = getState();
    
    if (!auth.token) {
      return rejectWithValue('未授权，请先登录');
    }
    
    try {
      // 这里应该有更新用户信息的 API 调用
      // 由于 api-go-api 中没有看到相关接口，先使用本地更新
      const updatedUser = { ...auth.user, ...newUserData };
      
      // 更新本地存储
      authService.saveAuthData(updatedUser, auth.token);
      
      return updatedUser;
    } catch (error) {
      logger.error('更新用户数据错误:', error);
      return rejectWithValue(error.message || '更新用户数据失败');
    }
  }
);

const initialState = {
  user: null,
  token: null,
  isLoggedIn: false,
  loading: true,
  error: null,
  codeSent: false,
  codeVerified: false,
  passwordReset: false,
  syncing: false,
  syncSuccess: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuthFlags: (state) => {
      state.codeSent = false;
      state.codeVerified = false;
      state.passwordReset = false;
    }
  },
  extraReducers: (builder) => {
    // 处理初始化认证状态
    builder.addCase(initAuth.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(initAuth.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isLoggedIn = !!action.payload.user;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(initAuth.rejected, (state, action) => {
      state.user = null;
      state.token = null;
      state.isLoggedIn = false;
      state.loading = false;
      state.error = action.payload || '认证初始化失败';
    });
    
    // 处理登录
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isLoggedIn = true;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || '登录失败';
    });
    
    // 处理注册
    builder.addCase(registerUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(registerUser.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      
      // 如果注册成功并返回了用户信息，则更新登录状态
      if (action.payload.user && action.payload.token) {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isLoggedIn = true;
      }
      // 如果只返回了注册成功的标志，则不改变登录状态
    });
    builder.addCase(registerUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || '注册失败';
    });
    
    // 处理获取用户资料
    builder.addCase(fetchUserProfile.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchUserProfile.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(fetchUserProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || '获取用户资料失败';
    });
    
    // 处理登出
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.user = null;
      state.token = null;
      state.isLoggedIn = false;
      state.error = null;
    });
    
    // 处理用户数据更新
    builder.addCase(updateUserData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateUserData.fulfilled, (state, action) => {
      state.user = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(updateUserData.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || '更新用户数据失败';
    });

    // 处理发送验证码
    builder.addCase(sendVerificationCode.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.codeSent = false;
    });
    builder.addCase(sendVerificationCode.fulfilled, (state) => {
      state.loading = false;
      state.codeSent = true;
      state.error = null;
    });
    builder.addCase(sendVerificationCode.rejected, (state, action) => {
      state.loading = false;
      state.codeSent = false;
      state.error = action.payload || '发送验证码失败';
    });

    // 处理验证验证码
    builder.addCase(verifyCode.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(verifyCode.fulfilled, (state) => {
      state.loading = false;
      state.codeVerified = true;
      state.error = null;
    });
    builder.addCase(verifyCode.rejected, (state, action) => {
      state.loading = false;
      state.codeVerified = false;
      state.error = action.payload || '验证码验证失败';
    });

    // 处理重置密码
    builder.addCase(resetPassword.pending, (state) => {
      state.loading = true;
      state.passwordReset = false;
      state.error = null;
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.loading = false;
      state.passwordReset = true;
      state.error = null;
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.loading = false;
      state.passwordReset = false;
      state.error = action.payload || '密码重置失败';
    });

    // 处理手动同步数据
    builder.addCase(syncUserData.pending, (state) => {
      state.syncing = true;
      state.error = null;
    });
    builder.addCase(syncUserData.fulfilled, (state) => {
      state.syncing = false;
      state.syncSuccess = true;
    });
    builder.addCase(syncUserData.rejected, (state, action) => {
      state.syncing = false;
      state.error = action.payload || '同步数据失败';
    });
  }
});

export const { clearError, clearAuthFlags } = authSlice.actions;
export default authSlice.reducer; 